import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  Button,
  Grid,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  Box
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreIcon from '@mui/icons-material/Restore';

import '../index.css';
import { mergePdf, getSelectedDocs } from '../services/services';
import { get_queue, get_tabs } from '../redux/actions/action';
import { Colors } from '../config/default';
import { useToast } from '../toast/toastContext';
import useMediaQuery from '@mui/material/useMediaQuery';
import '../image.css';
import { defaultFilter, getStatusKey, buildFilteredDocPayload } from '../services/common';
import { isEmpty } from 'lodash';
import { useUserRole } from '../hooks/useIdentity';

function SplitDoc({ handleClose, splitDocData, batchId, getbatches, openSplit }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(true);
  const [myData, setMyData] = useState(splitDocData);
  const smallScreen = useMediaQuery('(min-width:446px) and (max-width:824px)');
  const extraSmallScreen = useMediaQuery('(max-width:445px)');
  const erpType = useSelector((state) => state?.userInfo?.userInfo?.user?.erpType);
  const statementUpload = useSelector(
    (state) => state?.userInfo?.userInfo?.user?.statementUpload === true
  );
  const tableFilters = useSelector((state) => state?.table?.tableFilters);

  const validImageData = splitDocData?.filter(
    (item) => item.s3_file_name !== '$$' && item.s3_file_name !== '##'
  );
  let options = ['Invoice', 'Quote', 'G702', 'Receipt', 'Delivery Ticket'];
  // A statement inside a batch is often only recognised page by page here. It
  // needs no entry in toApiDocType: 'Statement' is already the backend's value.
  if (statementUpload) {
    options.push('Statement');
  }

  const userRole = useUserRole();
  const currentFilters = useSelector((state) => state?.filters?.filters);
  const savedFilters = !isEmpty(currentFilters) ? currentFilters : defaultFilter;
  const globalActiveTab = useSelector((state) => state?.tabs?.tabs);
  const callSource = userRole === 'Vendor' ? 'api' : 'app';

  const { showToast } = useToast();

  const [isEnlargedArray, setIsEnlargedArray] = useState(Array(validImageData?.length).fill(false));
  const handleImageClick = (index) => {
    const updatedEnlargedArray = [...isEnlargedArray];
    updatedEnlargedArray[index] = !updatedEnlargedArray[index];
    setIsEnlargedArray(updatedEnlargedArray);
  };
  const Tabs = {
    ...globalActiveTab,
    documentChildActiveTab: 'To Review'
  };
  useEffect(() => {
    const updatedData = [];
    splitDocData?.forEach((doc) => {
      let docType = doc?.docType;
      if (docType === 'Estimate') {
        docType = 'Quote';
      } else if (docType === 'DeliveryTicket') {
        docType = 'Delivery Ticket';
      } else if (docType === 'Other') {
        docType = 'Invoice';
      }

      doc?.files?.forEach((fileName, index) => {
        if (index === 0) {
          updatedData.push({
            s3_file_name: fileName,
            signedUrl: doc?.s3FileKeys[index],
            doc_type: docType
          });
        } else {
          updatedData.push({
            s3_file_name: '##',
            doc_type: ''
          });
          updatedData.push({
            s3_file_name: fileName,
            signedUrl: doc?.s3FileKeys[index],
            doc_type: ''
          });
        }
      });
      updatedData.push({
        s3_file_name: '$$',
        doc_type: ''
      });
    });
    if (updatedData?.length > 0) {
      updatedData.pop();
    }
    setMyData(updatedData);
  }, [splitDocData]);

  const handleSubmit = (item, index) => {
    const updatedData = [...myData];
    updatedData[index].s3_file_name = updatedData[index].s3_file_name === '$$' ? '##' : '$$';
    setMyData(updatedData);
    setReload(!reload);
  };

  const handleDropdownChange = (value, index) => {
    const updatedData = [...myData];
    updatedData[index].doc_type = value === '' ? 'Invoice' : value;
    setMyData(updatedData);
  };

  // Dropping a page outright, rather than splitting it off and deleting the
  // document afterwards. Asked for by Dawson (Kevin, 2026-09-11): "I really
  // want it to be split three ways and this cover page gets deleted... can I
  // delete a single page from here?" — the answer was submit first, then go
  // find the document and delete it.
  //
  // Excluded rather than spliced out: the array alternates page / separator, so
  // removing an element would have to remove a neighbouring separator too and
  // silently re-join two groups the user had split. Keeping the row also keeps
  // the decision reversible before Submit, which matters for a mis-click on a
  // scan the user cannot re-upload.
  const handleToggleExclude = (index) => {
    const updatedData = [...myData];
    updatedData[index] = { ...updatedData[index], excluded: !updatedData[index].excluded };
    setMyData(updatedData);
  };

  /**
   * The index of the page that owns its group's document-type dropdown: the
   * first page still included since the last separator. It moves when that page
   * is excluded, so the type stays reachable instead of disappearing with the
   * cover page.
   */
  const { groupLeadIndexes, inheritedDocType } = (() => {
    const leads = new Set();
    const inherited = new Map();
    let group = [];
    const closeGroup = () => {
      const lead = group.find((entry) => !entry.item.excluded);
      if (lead) {
        leads.add(lead.index);
        // The type the group already carried, even if the page that showed it
        // has just been removed — otherwise deleting a cover page quietly
        // reverts a Receipt or a Quote back to Invoice.
        const carried = group.find((entry) => entry.item.doc_type);
        if (carried) inherited.set(lead.index, carried.item.doc_type);
      }
      group = [];
    };
    (myData ?? []).forEach((item, index) => {
      if (item?.s3_file_name === '$$') {
        closeGroup();
        return;
      }
      if (item?.s3_file_name === '##') return;
      group.push({ item, index });
    });
    closeGroup();
    return { groupLeadIndexes: leads, inheritedDocType: inherited };
  })();

  const toApiDocType = (docType) => {
    if (docType === 'Quote') return 'Estimate';
    if (docType === 'Delivery Ticket') return 'DeliveryTicket';
    return docType;
  };

  /**
   * The payload groups. `$$` closes a group and `##` keeps it open — that is all
   * the two markers have ever meant, so the walk is just "split the page list at
   * every separator", with excluded pages skipped. A group whose pages are all
   * excluded produces nothing, which is what makes deleting a page work: the
   * backend creates one document per group it receives and never looks for the
   * pages it was not sent.
   */
  const buildGroups = (rows) => {
    const groups = [];
    let files = [];
    let docType = '';
    const flush = () => {
      if (files.length) {
        groups.push({ docType: toApiDocType(docType) || 'Invoice', files });
      }
      files = [];
      docType = '';
    };
    (rows ?? []).forEach((item, index) => {
      if (item?.s3_file_name === '$$') {
        flush();
        return;
      }
      if (item?.s3_file_name === '##' || item?.excluded) return;
      if (groupLeadIndexes.has(index)) {
        docType = item.doc_type || inheritedDocType.get(index) || '';
      }
      files.push(item.s3_file_name);
    });
    flush();
    return groups;
  };

  const handleClick = async () => {
    const pageSize = tableFilters?.dataGridPagination?.pageSize;

    const finalArrayData = buildGroups(myData);
    if (!finalArrayData.length) {
      // Every page was removed, so there is no document to create. The API
      // answers 400 SELECT_FILES for an empty list; say so here instead of
      // sending a request that cannot succeed.
      showToast('Keep at least one page to create a document.', 'error');
      return;
    }

    setLoading(true);

    const params = {
      data: finalArrayData,
      extractedDataId: batchId || ''
    };

    const result = await mergePdf(params);
    if (result?.data?.metadata?.status === 'SUCCESS') {
      showToast(result?.data?.metadata?.message, 'success');
      if (getbatches) {
        getbatches();
      }
      const response = await getSelectedDocs(
        '',
        buildFilteredDocPayload(tableFilters, savedFilters),
        callSource,
        false,
        1,
        getStatusKey(globalActiveTab?.documentChildActiveTab),
        pageSize
      );

      dispatch(get_tabs(Tabs));
      dispatch(get_queue(response?.data?.payload?.data));
      handleClose();
    } else {
      showToast(result?.response?.data?.metadata?.message, 'error');
    }

    setLoading(false);
  };
  const infoBox = [
    { color: '#429EB0', text: 'File Joiner' },
    { color: '#E95050', text: 'File Separator' }
  ];
  return (
    <>
      <Grid
        container
        sx={{
          paddingTop: '0px'
        }}>
        <Grid
          item
          xs={12}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
          <Typography sx={{ fontSize: '18px', color: Colors.WHITE }}>Split PDF</Typography>
          <Grid
            container
            item
            sx={{
              padding: '0 3rem',
              flexDirection: 'column',
              alignItems: 'flex-end',
              color: Colors.WHITE
            }}>
            {infoBox?.map((item, index) => (
              <Box
                key={index}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box
                  sx={{
                    width: '15px',
                    height: '15px',
                    backgroundColor: item?.color,
                    borderRadius: '4px',
                    mr: 1
                  }}
                />
                <Typography>{item?.text}</Typography>
              </Box>
            ))}
          </Grid>
        </Grid>
        <Grid
          container
          sx={{
            height: { xs: '450px', xl: '720px' },
            overflowY: 'scroll',
            margin: '0rem 3rem',
            borderRadius: '20px',
            padding: '.3rem 1rem',
            border: '2px dashed #5B5B5B'
          }}>
          {openSplit ? (
            <Grid container sx={{ alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <CircularProgress size={22} sx={{ color: Colors.DARKBLUE }} />
            </Grid>
          ) : (
            <>
              {myData?.map((item, index) => {
                const isSeparator = item.s3_file_name === '##' || item.s3_file_name === '$$';
                // The type belongs to the page leading the group, which moves
                // when a page is removed — so key this off the lead rather than
                // off position, or excluding a cover page would blank the type
                // of the page that inherits the dropdown.
                if (groupLeadIndexes.has(index)) {
                  if (!item.doc_type) {
                    item.doc_type = inheritedDocType.get(index) || 'Invoice';
                  }
                } else {
                  item.doc_type = '';
                }
                return (
                  <Grid
                    item
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      height: 'max-content',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}>
                    {/* One control row of a fixed height for every column —
                        page or separator, with a dropdown or without one — so
                        the pages and the separator bars below all hang off the
                        same baseline. Before, only a group's lead page had
                        anything here and the three things underneath each
                        carried their own `marginTop: 2.5rem` to compensate;
                        adding the remove button to every page made that
                        compensation double-count and knocked the joined page
                        out of line. */}
                    {/* No `xs` here on purpose. The column above is a COLUMN
                        flex container, so flex-basis governs the main axis —
                        height — and the `flex-basis: 100%` that `xs={12}`
                        applies silently beat the height below, collapsing this
                        row to nothing on a separator (which has no dropdown and
                        no button) and leaving the bar riding 2.5rem high. */}
                    <Grid
                      item
                      sx={{
                        mt: 1,
                        height: '2.5rem',
                        flexBasis: 'auto',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                      {/* The dropdown belongs to whichever page currently leads
                          the group, so removing a cover page hands it to the
                          next one instead of hiding the document type. */}
                      {groupLeadIndexes.has(index) && (
                        <Select
                          value={item.doc_type}
                          onChange={(e) => handleDropdownChange(e.target.value, index)}
                          sx={{ mb: 1, width: '150px', borderRadius: '10px', height: '2rem' }}>
                          {options.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                      {!isSeparator && (
                        <Tooltip title={item.excluded ? 'Keep this page' : 'Remove this page'}>
                          <IconButton
                            size="small"
                            aria-label={
                              item.excluded
                                ? `Keep page ${item.s3_file_name}`
                                : `Remove page ${item.s3_file_name}`
                            }
                            onClick={() => handleToggleExclude(index)}
                            sx={{ mb: 1, color: item.excluded ? Colors.DARKBLUE : '#E95050' }}>
                            {item.excluded ? (
                              <RestoreIcon fontSize="small" />
                            ) : (
                              <DeleteOutlineIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Grid>

                    {item.s3_file_name === '$$' ? (
                      <hr
                        style={{
                          border: 'none',
                          borderLeft: '8px solid #E95050',
                          height: smallScreen ? '290px' : extraSmallScreen ? '210px' : '300px',
                          margin: '0px',
                          cursor: 'pointer',
                          borderRadius: '30px'
                        }}
                        onClick={() => {
                          handleSubmit(item, index);
                        }}
                      />
                    ) : item.s3_file_name === '##' ? (
                      <hr
                        style={{
                          border: 'none',
                          borderLeft: '8px solid #429EB0',
                          height: smallScreen ? '290px' : extraSmallScreen ? '210px' : '300px',
                          cursor: 'pointer',
                          margin: '0px',
                          borderRadius: '30px'
                        }}
                        onClick={() => {
                          handleSubmit(item, index);
                        }}
                      />
                    ) : (
                      <Grid
                        item
                        className={`splitImage-container ${
                          isEnlargedArray[index] ? 'splitEnlarged' : ''
                        }`}
                        sx={{ position: 'relative' }}
                        onClick={() => handleImageClick(index)}>
                        {item.excluded && (
                          <Typography
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              backgroundColor: 'rgba(0,0,0,0.75)',
                              color: Colors.WHITE,
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontSize: '13px',
                              pointerEvents: 'none',
                              zIndex: 1
                            }}>
                            Removed
                          </Typography>
                        )}
                        <img
                          id="i"
                          style={{
                            // Dimmed rather than hidden, so the page can be put
                            // back before Submit.
                            opacity: item.excluded ? 0.25 : 1,
                            cursor: isEnlargedArray[index] ? 'zoom-out' : 'zoom-in',
                            width: isEnlargedArray[index]
                              ? 'auto'
                              : smallScreen
                              ? '250px'
                              : extraSmallScreen
                              ? '180px'
                              : '250px',
                            height: isEnlargedArray[index]
                              ? 'auto'
                              : smallScreen
                              ? '290px'
                              : extraSmallScreen
                              ? '210px'
                              : '300px',
                            marginTop: 'auto',
                            borderRadius: '10px'
                          }}
                          alt={item?.s3_file_name}
                          src={item?.signedUrl}
                        />
                      </Grid>
                    )}
                  </Grid>
                );
              })}
            </>
          )}
        </Grid>
        <Grid container sx={{ m: '1em 0em', padding: '1rem', justifyContent: 'center' }}>
          <Button
            disabled={loading}
            variant="contained"
            sx={{
              borderRadius: '10px',
              width: '7rem',
              height: '2.5rem',
              color: '#fff',
              textTransform: 'none',
              backgroundColor: Colors.DEEPBLUE,
              '&:hover': {
                backgroundColor: Colors.DEEPBLUE
              }
            }}
            onClick={handleClick}>
            {loading ? <CircularProgress size={25} color="inherit" /> : 'Submit'}
          </Button>
        </Grid>
      </Grid>
    </>
  );
}
export default SplitDoc;
